import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext.jsx';
import './App.css';
import StoreForm from './components/store-form/StoreForm';
import StoreView from './components/store-view/StoreView';
import StoreVerify from './components/store-verify/StoreVerify';
import Settings from './components/settings/Settings';

import BankDetails from './components/bank-details/BankDetails';
import BankSurvey from './components/bank-details/BankSurvey';
import TokenEdit from './components/bank-details/TokenEdit';
import CentralAccountMU from './components/bank-details/CentralAccountMU';
import MuCentralOffice from './components/bank-details/MuCentralOffice';
import Form402 from './components/StoreForm/402/402';
import Form403 from './components/StoreForm/403/403';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Login from './components/auth/Login';
import mwuLogo from './components/StoreForm/mwu-logo.webp';
import { hasNavigationPermission } from './utils/navigationPermissions';
import API_BASE_URL from './config/api.js';

// Main app component with tabs - now protected
const MainApp = () => {
  const [activeTab, setActiveTab] = useState('form');
  const [refreshTrigger] = useState(0);
  const { user, logout, permissions, fieldPermissions } = useAuth();

  // Debug permissions
  useEffect(() => {
    console.log('=== DEBUG PERMISSIONS ===');
    console.log('User:', user);
    console.log('Basic permissions:', permissions);
    console.log('Field permissions count:', fieldPermissions?.length || 0);
    console.log('Field permissions sample:', fieldPermissions?.slice(0, 5));
    
    // Test navigation permissions
    const navigationTests = [
      'add_data', 'view_data', 'verify_data', 'form402', 'form403', 'settings'
    ];
    
    console.log('Navigation permission tests:');
    navigationTests.forEach(nav => {
      const hasPermission = hasNavigationPermission(fieldPermissions, nav, user);
      console.log(`  ${nav}: ${hasPermission}`);
    });
    
    // API connection info
    console.log('=== API CONNECTION INFO ===');
    console.log('Current API URL from config:', API_BASE_URL);
    
    console.log('=== END DEBUG ===');
  }, [user, permissions, fieldPermissions]);

  // Set default active tab based on available permissions
  useEffect(() => {
    const availableTabs = [];
    
    // Include admin user in all permissions
    if (hasNavigationPermission(fieldPermissions, 'add_data', user)) {
      availableTabs.push('form');
    }
    if (hasNavigationPermission(fieldPermissions, 'view_data', user)) {
      availableTabs.push('view');
    }
    if (hasNavigationPermission(fieldPermissions, 'verify_data', user)) {
      availableTabs.push('verify');
    }
    if (hasNavigationPermission(fieldPermissions, 'form402', user)) {
      availableTabs.push('form402');
    }
    if (hasNavigationPermission(fieldPermissions, 'form403', user)) {
      availableTabs.push('form403');
    }
    if (hasNavigationPermission(fieldPermissions, 'settings', user)) {
      availableTabs.push('settings');
    }

    
    // Set the first available tab as active, or keep current if it's still available
    if (availableTabs.length > 0 && !availableTabs.includes(activeTab)) {
      setActiveTab(availableTabs[0]);
    }
  }, [fieldPermissions, activeTab]);

  const handleLogout = () => {
    logout();
  };

  return (
    <div className="App">
      <header className="App-header">
        <div className="header-top">
          <div className="logo">
            <img src={mwuLogo} alt="MWU Logo" className="logo-image" />
          </div>
          <div className="title-section">
            <h1>Mid-West University</h1>
            <h2>क्लाउड ल्याब स्टोर व्यवस्थापन प्रणाली</h2>
          </div>
          <div className="user-info">
            <span>Welcome, {user?.first_name || user?.username || 'User'}!</span>
            <button onClick={handleLogout} className="logout-btn">
              Logout
            </button>
          </div>
        </div>

        <nav className="nav-tabs">
          {hasNavigationPermission(fieldPermissions, 'add_data', user) && (
            <button 
              className={`nav-tab ${activeTab === 'form' ? 'active' : ''}`}
              onClick={() => setActiveTab('form')}
            >
              Add Data
            </button>
          )}
          {hasNavigationPermission(fieldPermissions, 'view_data', user) && (
            <button 
              className={`nav-tab view-data-btn ${activeTab === 'view' ? 'active' : ''}`}
              onClick={() => setActiveTab('view')}
            >
              📊 View Data
            </button>
          )}
          {hasNavigationPermission(fieldPermissions, 'verify_data', user) && (
            <button 
              className={`nav-tab verify-data-btn ${activeTab === 'verify' ? 'active' : ''}`}
              onClick={() => setActiveTab('verify')}
            >
              ✅ Verify Data
            </button>
          )}
          {hasNavigationPermission(fieldPermissions, 'form402', user) && (
            <button 
              className={`nav-tab form402-btn ${activeTab === 'form402' ? 'active' : ''}`}
              onClick={() => setActiveTab('form402')}
            >
              📋 402-Form
            </button>
          )}
          {hasNavigationPermission(fieldPermissions, 'form403', user) && (
            <button 
              className={`nav-tab form403-btn ${activeTab === 'form403' ? 'active' : ''}`}
              onClick={() => setActiveTab('form403')}
            >
              📋 403-Form
            </button>
          )}
          {hasNavigationPermission(fieldPermissions, 'settings', user) && (
            <button 
              className={`nav-tab settings-btn ${activeTab === 'settings' ? 'active' : ''}`}
              onClick={() => setActiveTab('settings')}
            >
              ⚙️ Settings
            </button>
          )}

        </nav>
      </header>
      
      <main className="App-main">
        {activeTab === 'form' && hasNavigationPermission(fieldPermissions, 'add_data', user) && <StoreForm />}
        {activeTab === 'view' && hasNavigationPermission(fieldPermissions, 'view_data', user) && <StoreView refreshTrigger={refreshTrigger} />}
        {activeTab === 'verify' && hasNavigationPermission(fieldPermissions, 'verify_data', user) && <StoreVerify refreshTrigger={refreshTrigger} />}
        {activeTab === 'form402' && hasNavigationPermission(fieldPermissions, 'form402', user) && <Form402 />}
        {activeTab === 'form403' && hasNavigationPermission(fieldPermissions, 'form403', user) && <Form403 />}
        {activeTab === 'settings' && hasNavigationPermission(fieldPermissions, 'settings', user) && <Settings />}

      </main>
    </div>
  );
};

// Protected Central Account MU component
const ProtectedCentralAccountMU = () => (
  <ProtectedRoute>
    <CentralAccountMU />
  </ProtectedRoute>
);

// Public Mu Central Office component (no protection needed)
const PublicMuCentralOffice = () => <MuCentralOffice />;

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          {/* Protected routes that require authentication */}
          <Route path="/" element={
            <ProtectedRoute>
              <MainApp />
            </ProtectedRoute>
          } />
          <Route path="/402-form" element={
            <ProtectedRoute>
              <Form402 />
            </ProtectedRoute>
          } />
          <Route path="/403-form" element={
            <ProtectedRoute>
              <Form403 />
            </ProtectedRoute>
          } />
          <Route path="/central-account-mu" element={<ProtectedCentralAccountMU />} />
          <Route path="/mu-central-office" element={<PublicMuCentralOffice />} />
          
          {/* Public routes that don't require authentication */}
          <Route path="/bank-survey" element={<BankSurvey />} />
          <Route path="/tokenedit" element={<TokenEdit />} />
          
          {/* Catch all route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
